import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuthStore } from '@/stores/auth';
import { Banner } from '@/components/ui/Banner';
import { StatCard } from '@/components/ui/StatCard';
import { deBelemApi } from '../api';
import type { DeBelemSummary } from '../types';

/**
 * Tab « De Belem » de /kpi - dashboard direction lecture seule alimenté
 * par l'API Bubble.io De Belem (cf src/server/modules/de-belem).
 *
 * Reçoit `dateFrom` / `dateTo` (YYYY-MM-DD) du shell parent KpiDirectionPage,
 * partage le DateRangePicker avec les autres tabs.
 */
export interface DeBelemContentProps {
  dateFrom: string;
  dateTo: string;
}

export function DeBelemContent({ dateFrom, dateTo }: DeBelemContentProps) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [data, setData] = useState<DeBelemSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    deBelemApi
      .summary(accessToken, { dateFrom, dateTo })
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError((err as Error)?.message ?? 'Erreur inconnue');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, dateFrom, dateTo]);

  const chartData = useMemo(() => buildChartData(data), [data]);
  const deltas = useMemo(() => computeDeltas(data), [data]);

  if (error) {
    return <Banner tone="danger">{`Impossible de charger les données De Belem : ${error}`}</Banner>;
  }

  if (loading && !data) {
    return (
      <div className="rounded-lg bg-white p-8 text-center text-sm text-text-muted">Chargement…</div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-lg bg-white p-8 text-center text-sm text-text-muted">
        Aucune donnée.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 5 StatCards en haut */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard
          label="Partenaires actifs"
          value={fmtCount(data.users.actifs)}
          secondaryBadge={{
            label: `${data.users.resilies} résilié${data.users.resilies > 1 ? 's' : ''}`,
            tone: data.users.resilies > 0 ? 'danger' : 'neutral',
          }}
        />
        <StatCard
          label="Prospects"
          value={fmtCount(data.users.prospects)}
          secondaryBadge={{
            label: `${data.users.perdus} perdu${data.users.perdus > 1 ? 's' : ''}`,
            tone: data.users.perdus > 0 ? 'danger' : 'neutral',
          }}
        />
        <StatCard
          label="Bordereaux"
          value={fmtCount(data.bordereaux.countCurrent)}
          delta={deltas.bordereauxCount}
          hint={`${fmtCount(data.bordereaux.countN1)} sur N-1`}
        />
        <StatCard
          label="CA TTC"
          value={fmtMoney(data.bordereaux.montantTtcEncaisse)}
          delta={deltas.ca}
          hint={
            data.bordereaux.montantTtcAVenir > 0
              ? `CA à venir : ${fmtMoney(data.bordereaux.montantTtcAVenir)}`
              : undefined
          }
        />
        <StatCard
          label="Commissions"
          value={fmtMoney(data.bordereaux.commissionsEncaissees)}
          delta={deltas.commissions}
          hint={
            data.bordereaux.commissionsAVenir > 0
              ? `Commissions à venir : ${fmtMoney(data.bordereaux.commissionsAVenir)}`
              : undefined
          }
        />
      </div>

      {/* Chart évolution cumulée CA + commissions, N vs N-1 */}
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-brand">
              Évolution cumulée CA &amp; commissions
            </h3>
            <p className="mt-1 text-xs text-text-muted">
              Bordereaux encaissés · cumul depuis le début de la période · N vs N-1
            </p>
          </div>
        </div>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#e3e8ee" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: '#8898aa' }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: '#8898aa' }}
                tickFormatter={(v: number) => fmtMoneyShort(v)}
                width={64}
              />
              <Tooltip
                formatter={(value) => (typeof value === 'number' ? fmtMoney(value) : String(value))}
                labelClassName="text-xs"
                contentStyle={{ fontSize: 11, borderRadius: 6 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line
                type="monotone"
                dataKey="caN"
                name="CA TTC (N)"
                stroke="var(--accent-hex)"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="caN1"
                name="CA TTC (N-1)"
                stroke="var(--accent-hex)"
                strokeOpacity={0.4}
                strokeDasharray="4 3"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="commN"
                name="Commissions (N)"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="commN1"
                name="Commissions (N-1)"
                stroke="#10b981"
                strokeOpacity={0.4}
                strokeDasharray="4 3"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top partenaires */}
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-brand">Top partenaires</h3>
          <p className="mt-1 text-xs text-text-muted">
            Par CA TTC encaissé sur la période · top 10
          </p>
        </div>
        {data.topPartenaires.length === 0 ? (
          <div className="py-6 text-center text-sm text-text-muted">
            Aucun bordereau encaissé sur cette période.
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-text-muted">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Partenaire</th>
                  <th className="px-4 py-3 text-right font-medium">CA TTC</th>
                  <th className="px-4 py-3 text-right font-medium">Commissions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.topPartenaires.map((p) => (
                  <tr key={p.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-brand">{p.raisonSociale}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {fmtMoney(p.montantTotalTTC)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-text-muted">
                      {fmtMoney(p.commissions)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtCount(n: number): string {
  return new Intl.NumberFormat('fr-FR').format(n);
}

function fmtMoney(n: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}

/** Format compact pour les ticks Y de chart (ex: "1,2 k €", "850 €"). */
function fmtMoneyShort(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M €`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)} k €`;
  return `${Math.round(n)} €`;
}

/**
 * Aligne current et N-1 sur un axe X commun par index, et calcule les
 * **valeurs cumulées** (convention « graphes CA cumulés » :
 * tous les line/area charts qui montrent du CA sont cumulés sur la
 * période - lecture immédiate de la trajectoire vs N-1).
 *
 * Pourquoi par index et pas par monthIso : la série courante et la série
 * N-1 peuvent avoir des longueurs différentes (la fenêtre N-1 est tronquée
 * d'1 mois côté backend pour absorber le lag de saisie des bordereaux).
 * En indexant par position, on compare "1er mois de la période" vs "1er
 * mois de N-1", "2e mois" vs "2e mois", etc. — la ligne N-1 s'arrête
 * naturellement où ses données s'arrêtent (null = gap dans la ligne).
 *
 * Cumul : à chaque mois, on ajoute la valeur du mois au total accumulé
 * depuis le début de la période. Le total final affiché = dernière valeur
 * de la série, **pas** une somme indépendante.
 */
function buildChartData(data: DeBelemSummary | null) {
  if (!data) return [];
  const n = data.evolution.current;
  const n1 = data.evolution.n1;
  const len = Math.max(n.length, n1.length);

  let cumCaN = 0;
  let cumCaN1 = 0;
  let cumCommN = 0;
  let cumCommN1 = 0;

  return Array.from({ length: len }, (_, i) => {
    const cur = n[i];
    const past = n1[i];
    if (cur) {
      cumCaN += cur.ca;
      cumCommN += cur.commissions;
    }
    if (past) {
      cumCaN1 += past.ca;
      cumCommN1 += past.commissions;
    }
    return {
      label: cur
        ? formatMonthLabel(cur.monthIso)
        : past
          ? formatMonthLabel(past.monthIso)
          : `M${i + 1}`,
      // Si le mois N n'a pas de données, on pose null (gap visible) plutôt
      // que de propager le cumul précédent — informatif sur la fin de la
      // série N ou N-1 (notamment N-1 truncated par le -1 mois).
      caN: cur ? cumCaN : null,
      caN1: past ? cumCaN1 : null,
      commN: cur ? cumCommN : null,
      commN1: past ? cumCommN1 : null,
    };
  });
}

function formatMonthLabel(monthIso: string): string {
  const [y, m] = monthIso.split('-');
  const months = [
    'janv.',
    'févr.',
    'mars',
    'avr.',
    'mai',
    'juin',
    'juil.',
    'août',
    'sept.',
    'oct.',
    'nov.',
    'déc.',
  ];
  const idx = parseInt(m ?? '1', 10) - 1;
  return `${months[idx] ?? ''} ${y?.slice(2) ?? ''}`;
}

/**
 * Calcule les badges de variation % vs N-1 pour les cards Bordereaux, CA
 * TTC et Commissions. Conventions :
 *  - prev=0 et curr=0 → undefined (pas de badge, pas pertinent)
 *  - prev=0 et curr>0 → badge gris « new »
 *  - prev>0 → variation arrondie au % entier, vert si >=0, rouge si <0
 */
function computeDeltas(data: DeBelemSummary | null) {
  if (!data) {
    return { bordereauxCount: undefined, ca: undefined, commissions: undefined };
  }
  // CA et commissions N-1 = somme des séries N-1 (cohérent avec
  // montantTtcEncaisse qui ne compte que les encaissés - les séries
  // appliquent le même gate côté backend).
  const caN1 = data.evolution.n1.reduce((sum, p) => sum + p.ca, 0);
  const commN1 = data.evolution.n1.reduce((sum, p) => sum + p.commissions, 0);
  return {
    bordereauxCount: deltaPct(data.bordereaux.countCurrent, data.bordereaux.countN1),
    ca: deltaPct(data.bordereaux.montantTtcEncaisse, caN1),
    commissions: deltaPct(data.bordereaux.commissionsEncaissees, commN1),
  };
}

function deltaPct(
  curr: number,
  prev: number,
): { value: string; direction: 'up' | 'down' | 'neutral' | 'new' } | undefined {
  if (prev === 0 && curr === 0) return undefined;
  if (prev === 0) return { value: 'new', direction: 'new' };
  const pct = ((curr - prev) / prev) * 100;
  const rounded = Math.round(pct);
  const direction = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'neutral';
  const sign = rounded > 0 ? '+' : '';
  return { value: `${sign}${rounded}%`, direction };
}
